// Vue
import { Vue, Component, Prop, Inject } from "vue-property-decorator";

// Template
import template from "./template.vue";

import ModuleWrapper from "#/components/module_wrapper/ModuleWrapper";
import InlineSVG from "#/components/inline_svg/InlineSVG";
import ModuleBase from "#/common/ModuleBase";
import { scrollToElementId } from "#/common/Utils";

@Component({
  mixins: [template],
  components: {
    ModuleWrapper,
    InlineSVG,
  },
})
export class StickyAnchorModule extends ModuleBase {
  public $refs!: {
    subNav: HTMLElement;
    subNavContainer: HTMLElement;
    subNavSide: HTMLElement;
  };

  public body: HTMLElement | null = null;
  public header!: HTMLElement;
  public isTouch: boolean = false;
  public mobileMenuOpen: boolean = false;
  public ticking: boolean = false;
  public resizeTaskId: number | null = null;
  public resizeDelay: number = 100;
  public subNavVisible: boolean = false;
  public windowHeight: number = 0;
  public subNavTop: number = 0;
  public subNavHeight: number = 0;
  public anchorSidebarData: Array<any> = [];
  public subNavIsSticky: boolean = false;
  public subNavSideOpen: boolean = false;

  public getBackgroundColorFromInstance(instance: object) {
    return instance["background"];
  }

  public async created() {
    this.body = document.querySelector("body");

    //check for touch
    if (this.body !== null) this.body.classList.add("sticky-anchor--no-touch");

    window.addEventListener("resize", this.handleResize);

    window.addEventListener("touchstart", this.handleTouch);
    window.addEventListener("touchend", this.handleTouch);
    window.addEventListener("touchmove", this.handleTouch);

    window.addEventListener("touchstart", this.handleTouchTargetOutside);
  }

  public mounted() {
    this.setSubNavData();
    window.addEventListener("scroll", this.watchSubnav);

    this.header = <HTMLElement>(
      document.querySelector(`.${this.$appPrefix}-header`)
    );
  }

  public destroyed() {
    window.removeEventListener("resize", this.handleResize);
    window.removeEventListener("scroll", this.watchSubnav);
    window.removeEventListener("touchstart", this.handleTouchTargetOutside);
  }

  public watchSubnav() {
    //throttle scroll calls
    if (!this.ticking) {
      window.requestAnimationFrame(() => {
        if (
          this.$refs.subNavContainer !== undefined &&
          window.innerWidth < 768
        ) {
          //subNav sticky, after passing the header
          this.subNavIsSticky =
            this.getWindowScrollTop() >= this.subNavTop + this.subNavHeight;

          //if header is sticky, add space and show subNav beneath
          if (
            this.header !== null &&
            this.header.classList.contains(`${this.$appPrefix}-header--sticky`)
          )
            this.$refs.subNav.style.transform =
              "translateY(" + this.header.clientHeight + "px)";
          else this.$refs.subNav.style.transform = "translateY(0)";
        }

        //scroll spy and activate nav
        let scrollTop = this.getWindowScrollTop() + this.windowHeight / 2;
        this.instance["buttons"]
          .filter(
            (b) =>
              b.button &&
              b.button.access_allowed &&
              b.button.href &&
              b.button.href.indexOf("#") > -1
          )
          .forEach((b) => {
            let id = b.button.href.split("#")[1];

            let el: HTMLElement | null = document.getElementById(id);

            if (el !== null) {
              let top = el.getBoundingClientRect().top + window.scrollY;
              if (top > 0 && top <= scrollTop) {
                let navItem: HTMLElement | null = document.querySelector(
                  '.subnav-side__link[data-target="' + id + '"]'
                );
                let navItemActive: HTMLElement | null = document.querySelector(
                  ".subnav-side__link.subnav-side__link--active"
                );

                if (navItemActive !== null)
                  navItemActive.classList.remove("subnav-side__link--active");

                if (navItem !== null)
                  navItem.classList.add("subnav-side__link--active");
              }
            }
          });

        this.ticking = false;
      });

      this.ticking = true;
    }
  }

  public setSubNavData() {
    if (this.$refs.subNavContainer !== undefined) {
      this.subNavTop =
        this.$refs.subNavContainer.getBoundingClientRect().top +
        this.getWindowScrollTop();
      this.subNavHeight = this.$refs.subNavContainer.clientHeight;
    }
    this.windowHeight = window.innerHeight;
    this.subNavVisible = window.innerWidth >= 768;

    //close menu on size change
    if (this.subNavVisible && this.mobileMenuOpen) this.closeMobileMenu();
  }

  public scrollToAnchor(
    anchor: string,
    checkForOpenSidebar: boolean,
    e: Event
  ) {
    if (checkForOpenSidebar && this.isTouch) {
      if (!this.subNavSideOpen) {
        e.preventDefault();

        this.subNavSideOpen = true;
        return false;
      } else {
        this.subNavSideOpen = false;
      }
    }

    if (this.anchorExists(anchor)) {
      let element: HTMLElement | null = document.querySelector(
        "#" + this.dataTarget(anchor)
      );
      if (element !== null) {
        e.preventDefault();
        scrollToElementId(this.dataTarget(anchor));
      }
    }
  }

  public anchorExists(anchor: string): boolean {
    return (
      this.dataTarget(anchor) !== null &&
      document.querySelector("#" + this.dataTarget(anchor)) !== null
    );
  }

  private handleResize() {
    if (this.resizeTaskId !== null) {
      clearTimeout(this.resizeTaskId);
    }

    this.resizeTaskId = <any>setTimeout(() => {
      this.resizeTaskId = null;
      this.setSubNavData();
    }, this.resizeDelay);
  }

  public handleTouch() {
    this.isTouch = true;

    if (this.body !== null)
      this.body.classList.remove("sticky-anchor--no-touch");

    window.removeEventListener("touchstart", this.handleTouch);
    window.removeEventListener("touchend", this.handleTouch);
    window.removeEventListener("touchmove", this.handleTouch);
  }

  public getWindowScrollTop() {
    let supportPageOffset = window.pageYOffset !== undefined;
    let isCSS1Compat = (document.compatMode || "") === "CSS1Compat";
    return supportPageOffset
      ? window.pageYOffset
      : isCSS1Compat
      ? document.documentElement.scrollTop
      : document.body.scrollTop;
  }

  public dataTarget(url) {
    if (
      (typeof url === "string" || url instanceof String) &&
      url.indexOf("#") > -1 &&
      url !== "#"
    )
      return url.split("#")[1];

    return null;
  }

  public toTop() {
    window.scroll({ top: 0, left: 0, behavior: "smooth" });

    if (this.isTouch) this.subNavSideOpen = false;
  }

  public openMobileMenu() {
    this.mobileMenuOpen = true;

    if (this.body !== null) this.body.classList.add("sticky-anchor--menu-open");
  }

  public closeMobileMenu() {
    this.mobileMenuOpen = false;

    if (this.body !== null)
      this.body.classList.remove("sticky-anchor--menu-open");
  }

  public handleTouchTargetOutside(event: TouchEvent) {
    if (
      this.subNavVisible &&
      event.target instanceof Node &&
      event.target !== undefined &&
      !this.$refs.subNavSide.contains(event.target)
    )
      this.subNavSideOpen = false;
  }
}
